/**
 * Commonly Used keys. There are many more, like a-z which can be used as plain strings
 */
export const keys = {
  Enter:     'Enter',
  ArrowUp:   'ArrowUp',   ArrowDown:  'ArrowDown',
  ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight',
  Home:      'Home',      End:        'End',
  PageUp:    'PageUp',    PageDown:   'PageDown',
  Space:     ' ',         Escape: 'Escape',
  Tab:       'Tab',       NotMatched: 'NotMatched',
  AnyMatched: 'AnyMatched'
};

export const modifiers = {
  Meta: 'metaKey', Control: 'ctrlKey', Shift: 'shiftKey'
};

/**
 * Service that allows for handling keypresses in an event handlers
 * Normalizes a few cross browser differences
 *
 * @example
 * import { KeybindingService, keys } from 'services/keybinding-service';
 *
 * const { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } = keys;
 *
 * document.querySelector('input').addEventHandler('keydown', function(e) {
 *   KeybindingService.with(e).bind(
 *     Enter, () => { // do something },                   // bind a single key
 *     [ ArrowUp, ArrowRight ], () => { // do something }, // bind many keys,
 *     /[a-zA-Z]/, () => { // do something with letters }, // can take a single or many regexes
 *     ['c', 'C'], () => { // do something },              // bind the c key with a string
 *     NotMatched, () => { // nothing matched },           // callback for none of the keys matching
 *     AnyMatched, () => { // some key was matched }       // callback for any single key matching
 *   );
 * }, false);
 */
export const KeybindingService = {
  /**
  * Sets the event object to use with `bind`
  * @returns {KeybindingService} Returns itself
  */
  with(e, property='key') {
    this.e = e;
    this.property = property;
    delete this.matchedAction;
    delete this.notMatchedAction;
    return this;
  },

  /**
  * Runs a set of callbacks if the given key matches
  *
  * The arguments are dynamic, so it can take n keys and n callbacks
  * Each argument is a pair of keys or an array of keys and the callback associated with those keys
  * bind(
  *   Enter, () => {},                  // callback bound to the Enter key
  *   [ArrowUp, ArrowRight], () => {},  // callback bound to ArrowUp and ArrowRight
  *   [/[a-zA-Z]/], () => {},           // callback bound to e.key matching any number of regexes
  *   NotMatched, () => {},             // callback if no key matched
  *   AnyMatched, () => {}              // callback if any key was matched
  * )
  */
  bind(...args) {
    args.forEach((argument, index) => {
      const keysValidation = [
        Array.isArray(argument),
        typeof argument === 'string',
        argument instanceof RegExp
      ];

      if((index % 2 === 0 && !keysValidation.some(v => v === true)) || (index % 2 == 1 && typeof argument !== 'function')) {
        throw new Error(`Argument validation failed: strings, array of strings, regexs, or array of regexes must be followed by their associated callback. Like this KeybindingService.with(e).bind(['c'], () => { // do something })`);
      }
    });
    return this.execute(
      args.reduce((result, _, index, array) => {  // transforms the arguments into key and callback pairs like [ [[...keys], callback] ]
        return index % 2 === 0 ? result.concat([array.slice(index, index + 2)]) : result;
      }, [])
    );
  },

  execute(args) {
    return ((values) => {  // normalizes the return type so that multiple callbacks
      return values.filter(v => v !== undefined).reduce((result, { matched, value }, index, array) => {
        if(matched) {
          result.push(value);
        }

        if(index + 1 === array.length) {  // last item?
          if(result.length > 0) {  // got some results, run the first AnyMatched
            const action = array.find(v => typeof v.matchedAction === 'function');
            if(action) {
              action.matchedAction(this.e);
            }
          } else {  // got no results, run the first NotMatched
            const action = array.find(v => typeof v.notMatchedAction === 'function');
            if(action) {
              action.notMatchedAction(this.e);
            }
          }
          if(result.length === 1) {  // normalize something like [ false ] to just false
            result = result[0];
          }
        }

        return result;
      }, []);
    })(args.map(([keyCodes, action], index, array) => {
      if(!Array.isArray(keyCodes)) {
        keyCodes = [ keyCodes ];  // if there's a single keycode make it into an array so it can be iterated
      }

      if(this.property === 'key') {  // normalize Internet Explorer specific values to W3C values
        Object.entries({
          [keys.ArrowUp]: 'Up',
          [keys.ArrowDown]: 'Down',
          [keys.ArrowLeft]: 'Left',
          [keys.ArrowRight]: 'Right',
          [keys.Space]: 'Spacebar'
        }).forEach(([key, internetExplorerKey]) => {
          if(keyCodes.includes(key)) {
            keyCodes.push(internetExplorerKey);
          }
        });
      }

      return keyCodes.reduce((result, code) => {
        if(code instanceof RegExp && code.test(this.e[this.property])) {
          result = action(this.e);
        } else if(Array.isArray(code)) {
          if(code.every(subcode => subcode === this.e[this.property] ||
              (Object.values(modifiers).includes(subcode) && this.e[subcode] === true))) {
            result.matched = true;
            result.value = action(this.e);
          }
        } else if(code === this.e[this.property]) {
          result.matched = true;
          result.value = action(this.e);
        } else if(code === keys.NotMatched) {
          result.notMatchedAction = action;
        } else if(code === keys.AnyMatched) {
          result.matchedAction = action;
        }
        return result;
      }, { matched: false });
    }));

  }
};
